import {
  AI_LEVEL_ID,
  DEFAULT_AGENT_POLICY,
  DEFAULT_BLOCKED_INTENTS,
  DEFAULT_ESCALATION_LADDER,
  type AgentConsents,
  type AgentPolicy,
  type AgentPolicyUpdate,
  type AiActivityEntry,
  type AiDraft,
  type ConversationState,
  type CustomerRecord,
  type EscalationLadder,
  type KnowledgeSource,
} from '@unipostudio/shared-types';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';

/**
 * MOCK ADAPTER for the agentic inbox. Stands in for endpoints that do not
 * exist yet (see docs/UI_REVAMP_API_GAPS.md). State lives in localStorage per
 * workspace, so the UI is fully usable and survives a reload — but it is this
 * browser only and never reaches the server or a teammate.
 *
 * To retire it: implement the endpoint, replace the matching call in
 * `inbox-agent.service.ts`, and delete the method here. When the last one
 * goes, delete this file.
 */

interface MockDb {
  policy: AgentPolicy;
  ladder: EscalationLadder;
  states: Record<string, Partial<ConversationState>>;
  activity: Record<string, AiActivityEntry[]>;
  sources: KnowledgeSource[];
  /** Notes and block flag per merged customer key (M04). */
  customers: Record<string, CustomerRecord>;
}

const PREFIX = 'unipostudio.mock.inbox-agent.';

/**
 * The defaults now live in shared-types, so this mock and the API return the
 * same thing for a workspace that has never saved a policy. Re-exported under
 * the old names because the settings screens and their tests import them here.
 */
export const DEFAULT_POLICY: AgentPolicy = DEFAULT_AGENT_POLICY;

/** A new workspace starts as AI → Level 1 (the workspace owner, shown when memberIds is empty). */
export const DEFAULT_LADDER: EscalationLadder = DEFAULT_ESCALATION_LADDER;

function storageKey(): string {
  return PREFIX + (useWorkspaceStore.getState().currentWorkspace?.id ?? 'no-workspace');
}

function fresh(): MockDb {
  return {
    policy: structuredClone(DEFAULT_POLICY),
    ladder: structuredClone(DEFAULT_LADDER),
    states: {},
    activity: {},
    sources: [],
    customers: {},
  };
}

function read(): MockDb {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return fresh();
    const parsed = JSON.parse(raw) as Partial<MockDb>;
    const base = fresh();
    return {
      policy: { ...base.policy, ...parsed.policy },
      ladder: parsed.ladder?.levels?.length ? parsed.ladder : base.ladder,
      states: parsed.states ?? {},
      activity: parsed.activity ?? {},
      sources: parsed.sources ?? [],
      customers: parsed.customers ?? {},
    };
  } catch {
    return fresh();
  }
}

function write(db: MockDb): void {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(db));
  } catch {
    // Storage unavailable: the mock simply does not persist.
  }
}

let seq = 0;
function id(prefix: string): string {
  seq += 1;
  return `${prefix}-${Date.now().toString(36)}-${seq}`;
}

function logActivity(db: MockDb, key: string, entry: Omit<AiActivityEntry, 'id' | 'at'>): void {
  const list = db.activity[key] ?? [];
  list.push({ ...entry, id: id('act'), at: new Date().toISOString() });
  db.activity[key] = list.slice(-40);
}

/** Resolve on the next tick, like a network call would. */
function later<T>(value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), 0));
}

const SAMPLE_REPLIES: Record<string, string> = {
  nl: 'Bedankt voor je bericht. We kijken het direct voor je na en komen zo snel mogelijk bij je terug.',
  de: 'Vielen Dank für Ihre Nachricht. Wir prüfen das umgehend und melden uns so schnell wie möglich bei Ihnen.',
  fr: 'Merci pour votre message. Nous vérifions cela tout de suite et revenons vers vous au plus vite.',
  es: 'Gracias por su mensaje. Lo revisamos ahora mismo y le respondemos lo antes posible.',
  it: 'Grazie per il messaggio. Verifichiamo subito e le rispondiamo il prima possibile.',
  pt: 'Obrigado pela sua mensagem. Vamos verificar de imediato e respondemos o mais breve possível.',
  en: 'Thanks for your message. We are checking this now and will get back to you as soon as we can.',
};

export const mockInboxAgent = {
  getPolicy: (): Promise<AgentPolicy> => later(read().policy),

  savePolicy(update: AgentPolicyUpdate): Promise<AgentPolicy> {
    const db = read();
    // As the server does: `true` records a consent, `false`/`null` withdraws it.
    const consents: AgentConsents = {};
    for (const [key, value] of Object.entries(update.consents ?? {})) {
      consents[key as keyof AgentConsents] =
        value === true ? { userId: 'mock-user', at: new Date().toISOString() } : value === false || value === null ? null : value ?? null;
    }
    const policy: AgentPolicy = { ...update, defaultMode: update.defaultMode === 'off' ? 'off' : 'suggest', consents };
    db.policy = policy;
    write(db);
    return later(policy);
  },

  getLadder: (): Promise<EscalationLadder> => later(read().ladder),

  saveLadder(ladder: EscalationLadder, reassignTo?: { fromLevelId: string; toLevelId: string }): Promise<EscalationLadder> {
    const db = read();
    db.ladder = ladder;
    if (reassignTo) {
      for (const state of Object.values(db.states)) {
        if (state.levelId === reassignTo.fromLevelId) state.levelId = reassignTo.toLevelId;
      }
    }
    write(db);
    return later(ladder);
  },

  getStates: (): Promise<Record<string, Partial<ConversationState>>> => later(read().states),

  patchState(key: string, patch: Partial<ConversationState>, activity?: Omit<AiActivityEntry, 'id' | 'at'>): Promise<Partial<ConversationState>> {
    const db = read();
    const next = { ...(db.states[key] ?? {}), ...patch, conversationKey: key };
    db.states[key] = next;
    if (activity) logActivity(db, key, activity);
    write(db);
    return later(next);
  },

  getActivity: (key: string): Promise<AiActivityEntry[]> => later(read().activity[key] ?? []),

  /** Log something a person did about the agent's work: intent feedback (A02) or a draft edit (A09). */
  addActivity(key: string, entry: Omit<AiActivityEntry, 'id' | 'at'>): Promise<AiActivityEntry[]> {
    const db = read();
    logActivity(db, key, entry);
    write(db);
    return later(db.activity[key] ?? []);
  },

  /**
   * Would the agent send this draft on its own? Mirrors the blueprint rule:
   * Auto mode, confidence at or above the threshold, intent not blocked.
   * The browser never sends automatically; this only labels the draft box.
   */
  autoSendAllowed(channel: string, intent: string | null, confidence: number | null): boolean {
    const { policy } = read();
    const mode = policy.perChannel[channel] ?? policy.defaultMode;
    const blocked = policy.blockedIntents ?? [...DEFAULT_BLOCKED_INTENTS];
    if (mode !== 'auto' || confidence === null || confidence < policy.autoSendThreshold) return false;
    return !(intent && blocked.includes(intent));
  },

  getCustomers: (): Promise<Record<string, CustomerRecord>> => later(read().customers),

  saveCustomerNotes(customerKey: string, notes: string): Promise<CustomerRecord> {
    const db = read();
    const record: CustomerRecord = { ...(db.customers[customerKey] ?? { customerKey, blocked: false, optedOut: false, optedOutAt: null, notes: '' }), notes, updatedAt: new Date().toISOString() };
    db.customers[customerKey] = record;
    write(db);
    return later(record);
  },

  setCustomerBlocked(customerKey: string, blocked: boolean): Promise<CustomerRecord> {
    const db = read();
    const record: CustomerRecord = { ...(db.customers[customerKey] ?? { customerKey, blocked: false, optedOut: false, optedOutAt: null, notes: '' }), blocked, updatedAt: new Date().toISOString() };
    db.customers[customerKey] = record;
    write(db);
    return later(record);
  },

  /** A plainly labelled sample, used only when the real AI endpoint is unavailable. */
  sampleDraft(key: string, language: string | null): Promise<AiDraft> {
    const lang = (language ?? 'en').split('-')[0] ?? 'en';
    const db = read();
    logActivity(db, key, { kind: 'classified', detail: 'general_question', confidence: 0.74 });
    logActivity(db, key, { kind: 'suggested', detail: null, confidence: 0.74 });
    write(db);
    return later({
      text: SAMPLE_REPLIES[lang] ?? SAMPLE_REPLIES['en']!,
      confidence: 0.74,
      language: lang,
      intent: 'general_question',
      sources: [],
      isSample: true,
    });
  },

  noteDraft(key: string, draft: AiDraft): Promise<void> {
    const db = read();
    if (draft.intent) logActivity(db, key, { kind: 'classified', detail: draft.intent, confidence: draft.confidence });
    for (const s of draft.sources) logActivity(db, key, { kind: 'knowledge_hit', detail: s.title, confidence: null });
    logActivity(db, key, { kind: 'suggested', detail: null, confidence: draft.confidence });
    write(db);
    return later(undefined);
  },

  listSources: (): Promise<KnowledgeSource[]> => later(read().sources),

  addSource(input: Pick<KnowledgeSource, 'kind' | 'title' | 'language' | 'content'>): Promise<KnowledgeSource> {
    const db = read();
    const source: KnowledgeSource = { ...input, id: id('kb'), status: 'ready', updatedAt: new Date().toISOString() };
    db.sources = [source, ...db.sources];
    write(db);
    return later(source);
  },

  removeSource(sourceId: string): Promise<void> {
    const db = read();
    db.sources = db.sources.filter((s) => s.id !== sourceId);
    write(db);
    return later(undefined);
  },

  /** Test helper. */
  reset(): void {
    try {
      localStorage.removeItem(storageKey());
    } catch {
      // ignore
    }
  },
};

export { AI_LEVEL_ID };
